// helpers existentes
function parseDate(v) {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

function toNumber(v) {
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
  return isNaN(n) ? 0 : n;
}

function normStr(v) {
  return (v || '').toString().trim();
}

function getMonthRange(periodYm) {
  const now = new Date();
  let y = now.getFullYear();
  let m = now.getMonth() + 1;
  if (periodYm && /^\d{4}-\d{2}$/.test(periodYm)) {
    const [yy, mm] = periodYm.split('-');
    y = Number(yy);
    m = Number(mm);
  }
  const start = new Date(Date.UTC(y, m - 1, 1, 0, 0, 0));
  const end = new Date(Date.UTC(y, m, 0, 23, 59, 59));
  return { year: y, month: m, start, end };
}

function isCollected(cert) {
  const tipo = normStr(cert.tipo_certificado).toLowerCase();
  if (tipo === 'suscripcion') {
    const st = normStr(cert.subscription_status).toLowerCase();
    return st === 'confirmada' || st === 'activa';
  }
  if (tipo === 'pago') {
    const paid = ['paid', 'success', 'completed'];
    const st = normStr(cert.estado_pago).toLowerCase();
    return paid.includes(st);
  }
  return false;
}

function isActiveInMonth(cert, start, end) {
  const inicio = parseDate(cert.fecha_inicio_cobertura);
  const termino = parseDate(cert.fecha_termino_cobertura);
  if (inicio && termino) {
    return inicio <= end && termino >= start;
  }
  // Fallback: si no hay cobertura, usar created_at o fecha_emision
  const created = parseDate(cert.created_at) || parseDate(cert.fecha_emision);
  return created ? created >= start && created <= end : false;
}

function getUfRateForDate(options, dateForRate) {
  if (options && typeof options.ufRateProvider === 'function') {
    const r = options.ufRateProvider(dateForRate);
    return typeof r === 'number' && r > 0 ? r : 0;
  }
  const r = options && typeof options.ufRate === 'number' ? options.ufRate : 0;
  return r > 0 ? r : 0;
}

// Nuevo helper: suscripción completa con contratación
function isSubscriptionCompleteWithContract(certOrSub) {
  const tipo = normStr(certOrSub?.tipo_certificado).toLowerCase();
  const status = normStr(certOrSub?.status || certOrSub?.subscription_status).toLowerCase();
  const hasContract = !!normStr(certOrSub?.contratacion_id);
  // En vistas: status de suscripción usa 'activa' / 'confirmada'
  return hasContract && (status === 'activa' || status === 'confirmada');
}

// Sumar charges opcionalmente para ARR (si se entregan por options)
function sumChargesForPeriod(charges, options, start, end) {
  if (!Array.isArray(charges) || charges.length === 0) {
    return { arrUF: 0, arrCLP: 0 };
  }
  const companyFilterId = options.companyId ? String(options.companyId) : null;
  const companyFilterName = options.companyName ? normStr(options.companyName).toLowerCase() : null;

  let arrUF = 0;
  let arrCLP = 0;
  for (const ch of charges) {
    // Filtrar por compañía si corresponde (company_subscription_charges)
    const chCompanyId = ch?.company_id != null ? String(ch.company_id) : null;
    const chCompanyName = normStr(ch?.compania_nombre);
    if (companyFilterId && chCompanyId !== companyFilterId) continue;
    if (companyFilterName && chCompanyName.toLowerCase() !== companyFilterName) continue;

    const cd = parseDate(ch?.charge_date) || parseDate(ch?.created_at);
    if (!cd || cd < start || cd > end) continue;
    const status = normStr(ch?.status).toLowerCase();
    const okStatuses = ['paid', 'processed', 'success', 'completed'];
    if (!okStatuses.includes(status)) continue;

    const amount = toNumber(ch?.amount);
    const currency = normStr(ch?.currency).toUpperCase();

    const rate = getUfRateForDate(options, cd);
    if (currency === 'UF') {
      arrUF += amount;
      arrCLP += rate > 0 ? amount * rate : 0;
    } else if (currency === 'CLP') {
      arrCLP += amount;
      arrUF += rate > 0 ? amount / rate : 0;
    }
  }
  return { arrUF, arrCLP };
}

export function computeCertificateMetrics(data, options = {}) {
  const { start, end, year, month } = getMonthRange(options.periodYm);

  const companyFilterId = options.companyId ? String(options.companyId) : null;
  const companyFilterName = options.companyName ? normStr(options.companyName).toLowerCase() : null;

  let primaTotalUF = 0;
  let primaTotalCLP = 0;
  let mrrUF = 0;
  let mrrCLP = 0;

  const byCompany = new Map();

  // Mapeo de certificados (company_certificados)
  const certificados = Array.isArray(data) ? data : [];
  const certById = new Map();
  for (const c of certificados) {
    certById.set(c.id, c);
  }

  // Prima recaudada desde company_certificados (tipo 'pago')
  for (const cert of certificados) {
    const companiaId = normStr(cert?.compania_id);
    const companiaNombre = normStr(cert?.compania_nombre);

    if (companyFilterId && companiaId !== companyFilterId) continue;
    if (companyFilterName && companiaNombre.toLowerCase() !== companyFilterName) continue;

    const tipo = normStr(cert?.tipo_certificado).toLowerCase();
    const collected = tipo === 'pago' && isCollected(cert);
    const montoUF = toNumber(cert?.monto);

    // Agregación por compañía
    const key = companiaId || companiaNombre || 'sin-compania';
    if (!byCompany.has(key)) {
      byCompany.set(key, {
        compania_id: companiaId || null,
        compania_nombre: companiaNombre || null,
        primaRecaudadaUF: 0,
        primaRecaudadaCLP: 0,
        mrrUF: 0,
        mrrCLP: 0,
        arrUF: 0,
        arrCLP: 0,
        count: 0,
      });
    }
    const agg = byCompany.get(key);
    agg.count += 1;

    if (collected) {
      const rate = getUfRateForDate(options, parseDate(cert?.fecha_emision) || start);
      primaTotalUF += montoUF;
      primaTotalCLP += montoUF * rate;
      agg.primaRecaudadaUF += montoUF;
      agg.primaRecaudadaCLP += montoUF * rate;
    }
  }

  // MRR desde company_suscripciones vinculando certificado vigente
  const suscripciones = Array.isArray(options?.company_suscripciones) ? options.company_suscripciones : [];
  for (const sub of suscripciones) {
    const status = normStr(sub?.status).toLowerCase();
    const isActiveConfirmed = status === 'activa' || status === 'confirmada';
    const hasContract = !!normStr(sub?.contratacion_id);
    const certId = sub?.certificado_id;
    const cert = certById.get(certId);

    // Filtrar compañía por suscripción o por certificado asociado
    const companiaId = cert?.compania_id != null ? String(cert.compania_id) : null;
    const companiaNombre = normStr(cert?.compania_nombre);
    if (companyFilterId && companiaId !== companyFilterId) continue;
    if (companyFilterName && companiaNombre.toLowerCase() !== companyFilterName) continue;

    if (!isActiveConfirmed || !hasContract || !cert) continue;
    if (!isActiveInMonth(cert, start, end)) continue;

    const amount = toNumber(sub?.amount);
    const currency = normStr(sub?.currency).toUpperCase();
    const rateForMonth = getUfRateForDate(options, start);

    let addUF = 0;
    let addCLP = 0;
    if (currency === 'UF') {
      addUF = amount;
      addCLP = rateForMonth > 0 ? amount * rateForMonth : 0;
    } else if (currency === 'CLP') {
      addCLP = amount;
      addUF = rateForMonth > 0 ? amount / rateForMonth : 0;
    } else {
      continue;
    }

    mrrUF += addUF;
    mrrCLP += addCLP;

    const key = companiaId || companiaNombre || 'sin-compania';
    if (!byCompany.has(key)) {
      byCompany.set(key, {
        compania_id: companiaId || null,
        compania_nombre: companiaNombre || null,
        primaRecaudadaUF: 0,
        primaRecaudadaCLP: 0,
        mrrUF: 0,
        mrrCLP: 0,
        arrUF: 0,
        arrCLP: 0,
        count: 0,
      });
    }
    const agg = byCompany.get(key);
    agg.mrrUF += addUF;
    agg.mrrCLP += addCLP;
  }

  // ARR: suma de company_subscription_charges del período
  const charges = Array.isArray(options?.company_subscription_charges)
    ? options.company_subscription_charges
    : [];
  let arrUF = 0;
  let arrCLP = 0;
  if (charges.length > 0) {
    const totals = sumChargesForPeriod(charges, options, start, end);
    arrUF = totals.arrUF;
    arrCLP = totals.arrCLP;
  } else {
    arrUF = mrrUF * 12;
    arrCLP = mrrCLP * 12;
  }

  // ARR por compañía: si hay charges, agrupar por company_id
  if (charges.length > 0) {
    const rateCacheDate = start;
    const okStatuses = new Set(['paid', 'processed', 'success', 'completed']);
    const rateForMonth = getUfRateForDate(options, rateCacheDate);
    const arrByCompany = new Map();
    for (const ch of charges) {
      const chDate = parseDate(ch?.charge_date) || parseDate(ch?.created_at);
      if (!chDate || chDate < start || chDate > end) continue;

      const status = normStr(ch?.status).toLowerCase();
      if (!okStatuses.has(status)) continue;

      const companiaId = ch?.company_id != null ? String(ch.company_id) : null;
      const companiaNombre = normStr(ch?.compania_nombre);
      if (companyFilterId && companiaId !== companyFilterId) continue;
      if (companyFilterName && companiaNombre.toLowerCase() !== companyFilterName) continue;

      const currency = normStr(ch?.currency).toUpperCase();
      const amount = toNumber(ch?.amount);

      const key = companiaId || companiaNombre || 'sin-compania';
      if (!arrByCompany.has(key)) {
        arrByCompany.set(key, { arrUF: 0, arrCLP: 0 });
      }
      const bucket = arrByCompany.get(key);

      if (currency === 'UF') {
        bucket.arrUF += amount;
        bucket.arrCLP += rateForMonth > 0 ? amount * rateForMonth : 0;
      } else if (currency === 'CLP') {
        bucket.arrCLP += amount;
        bucket.arrUF += rateForMonth > 0 ? amount / rateForMonth : 0;
      }
    }
    for (const [key, agg] of byCompany.entries()) {
      const arr = arrByCompany.get(key);
      if (arr) {
        agg.arrUF = arr.arrUF;
        agg.arrCLP = arr.arrCLP;
      } else {
        // fallback 12×MRR si no hay cargos para esa compañía
        agg.arrUF = agg.mrrUF * 12;
        agg.arrCLP = agg.mrrCLP * 12;
      }
    }
  } else {
    // sin cargos: fallback global ya calculado; por compañía mantener 12×MRR
    for (const agg of byCompany.values()) {
      agg.arrUF = agg.mrrUF * 12;
      agg.arrCLP = agg.mrrCLP * 12;
    }
  }

  const porCompania = Array.from(byCompany.values());

  return {
    summary: {
      primaTotalUF,
      primaTotalCLP,
      mrrUF,
      mrrCLP,
      arrUF,
      arrCLP,
    },
    porCompania,
    period: { year, month },
  };
}